<template>
    <div class="form-page text-center d-flex justify-content-center align-items-center">
        <main class="form-signin">
            <div class="w-100 d-flex justify-content-center mb-4 pb-4">
                <BrandLogo
                    class="logo-img d-flex justify-content-center"
                    variant="form"
                    transparent
                />
            </div>

            <div
                class="card card-small-box"
                :style="errors.length > 0 ? 'max-height: 500px' : 'max-height: 450px'"
            >
                <div v-if="isBusy" class="spinner-border text-danger m-auto" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <div v-else>
                    <div class="right-container-box my-4">
                        <h2>Nice to see you!</h2>
                    </div>
                    <div
                        v-for="(error, index) in errors"
                        :key="index"
                        class="alert alert-danger"
                        role="alert"
                    >
                        {{ error }}
                    </div>
                    <form @submit.prevent="login">
                        <font-awesome-icon :icon="['fas', 'coffee']" />
                        <div class="w-100 mt-2">
                            <input
                                type="text"
                                v-model="form.username"
                                placeholder="Username"
                                @keydown="clearField('username')"
                            />
                        </div>
                        <div class="w-100 mt-2">
                            <input
                                :type="passwordType"
                                v-model="form.password"
                                placeholder="Password"
                                @keydown="clearField('password')"
                            />
                            <span class="field-icon" @click="togglePassword()">
                                <font-awesome-icon
                                    :icon="['fas', 'eye-slash']"
                                    v-if="showPassword"
                                />
                                <font-awesome-icon :icon="['fas', 'eye']" v-else />
                            </span>
                        </div>
                        <div v-if="!isSearchDemo" class="w-100 mt-2">
                            <input
                                type="text"
                                v-model="form.key_2fa"
                                placeholder="2FA Key"
                                @keydown="clearField('key_2fa')"
                            />
                        </div>
                        <div class="buttons d-flex justify-content-between">
                            <div>
                                <strong>
                                    <a href="javascript:;" @click="goTo('forgot-password')"
                                        >Forgot password?</a
                                    >
                                </strong>
                            </div>
                            <button type="submit" class="login-button">Login</button>
                        </div>
                        <div class="buttons d-flex justify-content-between">
                            <button
                                v-if="!isInvoiceDashboard && !isSearchDemo"
                                type="button"
                                @click="goTo('sign-up')"
                                class="register-button"
                            >
                                Sign Up
                            </button>
                            <button
                                v-if="!isSearchDemo"
                                type="button"
                                @click="goTo('setup-2fa-key')"
                                class="register-button"
                            >
                                Need a 2FA Key?
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
    import 'bootstrap/dist/css/bootstrap.min.css'
    import '@/assets/form-style.css'
    import { toast } from 'vue3-toastify'
    import config from '@/services/config'
    import BrandLogo from '@/components/shared/BrandLogo.vue'

    import { LOGIN } from '@/data/url'

    import { mapStores } from 'pinia'
    import { useUserStore } from '@/stores/userStore'

    export default {
        name: 'LoginPage',
        components: { BrandLogo },
        data() {
            return {
                backendURL: process.env.VUE_APP_BACKEND_URL,
                errors: [],
                form: {
                    username: '',
                    password: '',
                    key_2fa: '',
                },
                showPassword: false,
                loading: false,
            }
        },
        computed: {
            ...mapStores(useUserStore),
            passwordType() {
                return this.showPassword ? 'text' : 'password'
            },
            isBusy() {
                return this.loading || this.userStore.initializing
            },
            isInvoiceDashboard() {
                return config.isInvoiceDashboard()
            },
            isSearchDemo() {
                return config.isSearchDemo()
            },
        },
        methods: {
            async login() {
                if (this.loading) {
                    return
                }

                this.errors = []

                if (!this.form.username) {
                    toast.error('The username is required.', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                if (!this.form.password) {
                    toast.error('The password is required.', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                if (!this.isSearchDemo && !this.form.key_2fa) {
                    toast.error('The key_2fa is required.', {
                        position: 'top-right',
                        autoClose: 2000,
                    })
                    return
                }

                if (this.errors.length) {
                    return
                }

                this.loading = true

                try {
                    const url = this.backendURL + LOGIN
                    const response = await fetch(url, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            ...config.getSubdomainHeader(),
                        },
                        body: JSON.stringify({
                            username: this.form.username,
                            password: this.form.password,
                            key_2fa: this.form.key_2fa,
                        }),
                    })

                    const data = await response.json()

                    if (!response.ok || data?.success === false) {
                        this.errors.push(data.error || 'Login failed. Please try again.')
                        this.loading = false
                    } else {
                        // The API may be hosted on a different Greenwood host.
                        // Store the returned token on the page host so the
                        // host-only authentication boundary still works.
                        if (data?.token) {
                            config.setCookie('token', data.token)
                        }
                        if (
                            this.isInvoiceDashboard &&
                            (!data?.user?.is_active || !data?.user?.is_staff)
                        ) {
                            await this.$router.replace({
                                name: 'invoice-access-denied',
                            })
                            return
                        }
                        const redirectTo =
                            this.userStore.getRedirectAfterLoginPath ||
                            (this.isInvoiceDashboard ? 'invoice-overview' : 'dashboard')
                        this.userStore.setRedirectAfterLoginPath(null)
                        if (redirectTo.startsWith('/')) {
                            const [path, queryString] = redirectTo.split('?')
                            let query = {}
                            if (queryString) {
                                query = Object.fromEntries(new URLSearchParams(queryString))
                            }
                            await this.$router.replace({ path, query })
                        } else {
                            await this.$router.replace({ name: redirectTo })
                        }
                    }
                } catch (error) {
                    this.errors.push('An unexpected error occurred. Please try again later.')
                    console.error(error)
                    this.loading = false
                }
            },
            clearField(field) {
                const newErrors = this.errors.filter((item) => item != `The ${field} is required.`)
                this.errors = newErrors
            },
            goTo(destiny) {
                this.$router.push({ name: destiny })
            },
            togglePassword() {
                this.showPassword = !this.showPassword
            },
        },
    }
</script>
